'use client';

import { useEffect, useState } from 'react';
import { getTestimonials } from '@/lib/actions/testimonials';
import { stripHtml } from '@/lib/utils';
import { Link } from '@/i18n/routing';
import {
  Plus,
  Pencil,
  Quote,
  Building2,
  User as UserIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DeleteTestimonialButton } from '@/components/admin/testimonials/DeleteTestimonialButton';

export default function AdminTestimonialsPage() {
  const [testimonials, setTestimonials] = useState<Awaited<ReturnType<typeof getTestimonials>>>([]);

  useEffect(() => {
    getTestimonials().then(setTestimonials);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-navy">Testimonials</h1>
          <p className="text-muted-foreground mt-1">Manage "What Our Clients Say" section on the homepage.</p>
        </div>
        <Button asChild className="bg-navy hover:bg-primary">
          <Link href="/admin/testimonials/new">
            <Plus className="mr-2 h-4 w-4" /> Add Testimonial
          </Link>
        </Button>
      </div>

      <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-gray-50/50">
            <TableRow>
              <TableHead className="w-[100px]">Order</TableHead>
              <TableHead>Client Details</TableHead>
              <TableHead>Message</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {testimonials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-20 text-gray-500">
                  <div className="flex flex-col items-center gap-3">
                    <div className="bg-gray-100 p-4 rounded-full">
                      <Quote size={40} className="text-gray-300" />
                    </div>
                    <p className="text-lg font-medium text-gray-400">No testimonials yet</p>
                    <p className="text-sm">Click the button above to add your first client review.</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              testimonials.map((item) => (
                <TableRow key={item.id} className="hover:bg-gray-50/50">
                  <TableCell className="font-medium text-gray-500">
                    #{item.order}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 font-bold text-navy">
                        <UserIcon size={14} className="text-primary" /> {item.name}
                      </div>
                      <div className="text-xs text-muted-foreground font-normal ml-5">
                        {item.position}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-primary font-medium">
                        <Building2 size={14} /> {item.company}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[400px]">
                    <p className="text-sm text-gray-600 line-clamp-2 italic">
                      &ldquo;{stripHtml(item.message)}&rdquo;
                    </p>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" asChild className="hover:bg-blue-50 hover:text-blue-600">
                        <Link href={`/admin/testimonials/${item.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      <DeleteTestimonialButton id={item.id} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
